/**
 * Duty, location sharing and safety check-ins for the field app (spec §14).
 * Location is shared only while on duty (MOB-003) and stops on "Go off duty"
 * or sign-out. The check-in timer runs on the server, so dispatch is alerted
 * even if this phone dies or loses signal.
 */
import { onSerialFix, serialIsFresh } from './serialgps.ts';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { distanceFeet } from '@civilos/domain';
import { api, ApiError } from '../shared/api.ts';
import { fmtDateTime } from '../shared/format.ts';
import { Badge, ErrorBox, useAction } from '../shared/ui.tsx';

export interface DutyView {
  onDuty: boolean;
  status: 'off' | 'available' | 'on_scene' | 'unavailable';
  statusSince: string;
  unavailableReason: string | null;
  onScene: { paperId: string; civilosNumber?: string; address?: string } | null;
  checkIn: { phase: 'ok' | 'checkin_due' | 'overdue' | 'paused'; reason: 'inactive' | 'on_scene' | null; dueAt: string | null; alertAt: string | null; dispatchAcknowledged: boolean };
  settings: { inactivityMinutes: number; onSceneMinutes: number; answerMinutes: number };
}

interface DutyCtx {
  duty: DutyView | null;
  error: string | null;
  gpsError: string | null;
  set: (v: DutyView) => void;
  call: (url: string, body?: unknown) => Promise<DutyView | undefined>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<DutyCtx>(null as never);
export const useDuty = () => useContext(Ctx);

const HEARTBEAT_MS = 60_000;
const SEND_ON_MOVE_M = 25;

export function DutyProvider({ children, online }: { children: ReactNode; online: boolean }) {
  const [duty, setDuty] = useState<DutyView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const lastPos = useRef<{ lat: number; lng: number; accuracy: number } | null>(null);
  const lastSent = useRef<{ lat: number; lng: number; at: number } | null>(null);

  const call = useCallback(async (url: string, body?: unknown) => {
    try {
      const v = body === undefined && url === '/api/field/duty' ? await api.get<DutyView>(url) : await api.post<DutyView>(url, body ?? {});
      setDuty(v);
      setError(null);
      return v;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No connection — duty status can’t update right now. Dispatch still gets alerts if you go quiet.');
      throw e;
    }
  }, []);

  const refresh = useCallback(async () => {
    await call('/api/field/duty').catch(() => undefined);
  }, [call]);

  useEffect(() => {
    if (online) refresh();
  }, [online, refresh]);

  const send = useCallback(async () => {
    const p = lastPos.current;
    if (!p) return refresh();
    lastSent.current = { lat: p.lat, lng: p.lng, at: Date.now() };
    await call('/api/field/location', p).catch(() => undefined);
  }, [call, refresh]);

  // Share location only while on duty.
  const onDuty = !!duty?.onDuty;
  useEffect(() => {
    if (!onDuty) return;
    let watch: number | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let offSerial: (() => void) | null = null;
    if ('geolocation' in navigator) {
      let lastFix = 0;
      let asking = false;
      const onFix = (pos: { coords: { latitude: number; longitude: number; accuracy: number } }, fromSerial = false) => {
          if (!fromSerial && serialIsFresh()) return;
          lastFix = Date.now();
          setGpsError(null);
          lastPos.current = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) };
          const s = lastSent.current;
          const movedM = s ? distanceFeet(s, lastPos.current) / 3.28084 : Infinity;
          if (movedM > SEND_ON_MOVE_M || !s || Date.now() - s.at > HEARTBEAT_MS) send();
      };
      const onErr = (e: GeolocationPositionError) => { if (e.code !== e.TIMEOUT) setGpsError(e.message || 'Location unavailable'); };
      offSerial = onSerialFix((f) => onFix({ coords: { latitude: f.lat, longitude: f.lng, accuracy: f.accuracyM ?? 5 } }, true));
      watch = navigator.geolocation.watchPosition(onFix, onErr, { enableHighAccuracy: true, maximumAge: 5_000, timeout: 30_000 });
      // Some laptops stop sending watch updates while driving: ask directly when nothing has come in for 10 s.
      poll = setInterval(() => {
        if (asking || Date.now() - lastFix < 10_000) return;
        asking = true;
        navigator.geolocation.getCurrentPosition((p) => ((asking = false), onFix(p)), (e) => ((asking = false), onErr(e)), { enableHighAccuracy: true, maximumAge: 3_000, timeout: 20_000 });
      }, 10_000);
    } else setGpsError('This device has no location service');
    const t = setInterval(send, HEARTBEAT_MS);
    return () => {
      if (watch != null) navigator.geolocation.clearWatch(watch);
      if (poll) clearInterval(poll);
      offSerial?.();
      clearInterval(t);
    };
  }, [onDuty, send]);

  return <Ctx.Provider value={{ duty, error, gpsError, set: setDuty, call, refresh }}>{children}</Ctx.Provider>;
}

/** Strip under the header: go on/off duty, availability. */
export function DutyBar() {
  // The header keys (UNAVAILABLE / ON DUTY / OFF DUTY) show and change duty status; this strip only
  // appears for what they can't show: out of the car at a door, or location / connection trouble.
  const { duty, error, gpsError, call } = useDuty();
  const a = useAction();
  if (!duty) return error ? <div className="duty-bar off small">{error}</div> : null;
  const scene = duty.onDuty && duty.status === 'on_scene';
  const unavailable = duty.onDuty && duty.status === 'unavailable';
  if (!scene && !unavailable && !(duty.onDuty && gpsError) && !error) return null;
  return (
    <div className={`duty-bar ${duty.onDuty ? duty.status : 'off'}`}>
      <span className="grow">
        {scene && <strong>Out of car{duty.onScene?.address ? ` · ${duty.onScene.address}` : ''}</strong>}
        {unavailable && <strong>Unavailable · {duty.unavailableReason ?? ''}</strong>}
        {duty.onDuty && gpsError && <span className="small">Location is off, so dispatch can’t see where you are. Turn on location for CivilHound.</span>}
        {error && <span className="small"> · {error}</span>}
      </span>
      {scene && (
        <button className="btn sm" disabled={a.busy} onClick={() => a.run(() => call('/api/field/status', { status: 'available' }))}>
          Back in car
        </button>
      )}
    </div>
  );
}

/**
 * Full-screen "Are you OK?" when a check-in is due (SAFE-010). The deputy gets the agency's answer time
 * (default 2 minutes) to tap "I'm OK" before dispatch is alerted; dispatch hears nothing before then.
 * The phone opens the prompt on its own at the due time rather than waiting for the next heartbeat,
 * so the deputy gets the full two minutes.
 */
export function CheckInOverlay() {
  const { duty, call, refresh } = useDuty();
  const a = useAction();
  const [now, setNow] = useState(() => Date.now());
  const dueAt = duty?.checkIn.dueAt ? Date.parse(duty.checkIn.dueAt) : null;
  const alertAt = duty?.checkIn.alertAt ? Date.parse(duty.checkIn.alertAt) : null;
  const serverPhase = duty?.checkIn.phase;
  // Wake at the due time and at the alert time to re-check with the server.
  useEffect(() => {
    if (!duty?.onDuty || serverPhase === 'paused') return;
    const timers = [dueAt, alertAt]
      .filter((t): t is number => t != null && t > Date.now())
      .map((t) => setTimeout(() => (setNow(Date.now()), refresh()), t - Date.now() + 500));
    return () => timers.forEach(clearTimeout);
  }, [duty?.onDuty, serverPhase, dueAt, alertAt, refresh]);
  // Past the due time but the server hasn't answered (no signal): ask anyway.
  const localDue = serverPhase === 'ok' && dueAt != null && now >= dueAt + 5_000;
  const phase = localDue ? (alertAt != null && now >= alertAt ? 'overdue' : 'checkin_due') : serverPhase;
  const active = phase === 'checkin_due' || phase === 'overdue';
  useEffect(() => {
    if (!active) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const buzz = () => navigator.vibrate?.([400, 200, 400]);
    buzz();
    const t = setInterval(buzz, 15_000);
    notifyPhone();
    return () => (clearInterval(tick), clearInterval(t));
  }, [active]);
  if (!active || !duty) return null;
  const onScene = duty.checkIn.reason === 'on_scene';
  const left = alertAt != null ? Math.max(0, Math.ceil((alertAt - now) / 1000)) : null;
  return (
    <div className={`checkin-overlay ${phase}`} role="alertdialog" aria-modal="true" aria-labelledby="checkin-title">
      <div className="checkin-box">
        <h1 id="checkin-title">Are you OK?</h1>
        <p>
          {onScene ? `You’ve been out of the car ${duty.onScene?.address ? `at ${duty.onScene.address} ` : ''}with no activity for ${duty.settings.onSceneMinutes}+ minutes.` : `No activity for ${duty.settings.inactivityMinutes}+ minutes.`}
        </p>
        {phase === 'overdue' ? (
          <p className="checkin-alerted">
            Dispatch has been alerted.{' '}
            {duty.checkIn.dispatchAcknowledged ? <Badge tone="ok">Dispatch acknowledged</Badge> : <Badge tone="warn">Waiting for dispatch</Badge>}
          </p>
        ) : (
          <p className="checkin-countdown" aria-live="polite">
            Dispatch will be alerted in <strong className="mono">{left == null ? '—' : `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`}</strong> unless you tap I’m OK.
          </p>
        )}
        <button className="btn primary big checkin-ok" disabled={a.busy} onClick={() => a.run(() => call('/api/field/ok'))}>
          I’m OK
        </button>
        <ErrorBox error={a.error} />
      </div>
    </div>
  );
}

/** A system notification so the prompt is noticed when the app isn't on screen (if the deputy allowed notifications). */
function notifyPhone() {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || document.visibilityState === 'visible') return;
    const opts = { body: 'Tap to answer — dispatch will be alerted if you don’t.', tag: 'civilhound-checkin', requireInteraction: true, vibrate: [400, 200, 400] } as NotificationOptions;
    navigator.serviceWorker?.ready.then((r) => r.showNotification('Are you OK?', opts)).catch(() => new Notification('Are you OK?', opts));
  } catch {
    /* notifications unavailable */
  }
}

/** Ask once, when the deputy goes on duty, so the check-in can reach them with the app in the background. */
export function askNotificationPermission() {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') void Notification.requestPermission();
  } catch {
    /* ignore */
  }
}

/** On a paper: "I'm at the door" (out of vehicle, INTG-003) / back in car. */
export function OnSceneControls({ paperId, address }: { paperId: string; address: string }) {
  const { duty, call } = useDuty();
  const a = useAction();
  if (!duty?.onDuty) return <p className="small muted">Go on duty (top of screen) to let dispatch know when you’re at a door.</p>;
  const here = duty.status === 'on_scene' && duty.onScene?.paperId === paperId;
  if (here)
    return (
      <div className="onscene-banner">
        <span className="grow">
          <strong>Out of car here</strong> since {fmtDateTime(duty.statusSince)}. Dispatch can see you. Recording the attempt puts you back in the car.
        </span>
        <button className="btn sm" disabled={a.busy} onClick={() => a.run(() => call('/api/field/status', { status: 'available' }))}>
          Back in car
        </button>
      </div>
    );
  return (
    <>
      <button className="btn big" disabled={a.busy} onClick={() => a.run(() => call('/api/field/on-scene', { paperId }))} title={`Tell dispatch you are out of the car at ${address}`}>
        I’m at the door (out of car)
      </button>
      <ErrorBox error={a.error} />
    </>
  );
}
